/* SSi website – /alw: Adult Learners' Week promo landing page.
 *
 * Shareable short URL for Nick's Adult Learners' Week promotion. The whole
 * point of this page is stats hygiene: this cohort must not pollute the
 * offer-test control arm or the channel stats, so every CTA carries
 * s=nick-alw and the source is stored for the session on mount (Pay falls
 * back to the stored value when its URL has no ?s= – see
 * attribution/source.js). Conversions land in Ivan's campaigns dashboard
 * under source 'nick-alw', cleanly separable from everything else.
 */
import { useEffect } from 'react';
import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { TestimonialFeature } from '../components/Testimonials.jsx';
import { AlwHero, AlwWhy, AlwOffer, AlwTickStyleInjector, ALW_SOURCE } from '../components/Alw.jsx';
import { storeSource } from '../attribution/source.js';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',
  alwKicker: "Adult Learners' Week",
  alwTitle: "It's never too late to start speaking.",
  alwLede: "Always meant to learn a language? Adult Learners' Week is the perfect excuse to actually start. SaySomethingin gets adults talking out loud from the very first session – no classrooms, no homework, no grammar drills. Just you, speaking.",
  alwCta: 'Start your free 30 days',
  alwTrust: "No card details, no payment – your first 30 days are completely free.",
  alwWhyKicker: 'Why SaySomethingin',
  alwWhyTitle: 'Built for busy adult brains.',
  alwWhy1Title: 'Speaking from minute one',
  alwWhy1Body: "You don't study the language, you use it. Our method has you building real spoken sentences in your first session – out loud, from memory, in your own voice.",
  alwWhy2Title: 'No homework, no memorising',
  alwWhy2Body: "The method handles the repetition and the spacing for you. You just press play and respond – half an hour whenever it fits around work, family and everything else.",
  alwWhy3Title: 'Mistakes are the method',
  alwWhy3Body: "You'll get things wrong, and that's the point – every mistake you notice is your brain learning. No red pen, no test anxiety, no being 'bad at languages'. That was never true anyway.",
  testiKicker: 'A word from Ruth Jones',
  testiQuote: "It's absolutely brilliant, it's really good fun, it's really easy to use - I don't know how it works but it does. I can't recommend it highly enough.",
  testiName: 'Ruth Jones',
  testiRole: 'Actor & writer (Gavin & Stacey)',
  testiCredit: 'As seen on BBC Radio 4',
  testiMoreLink: 'Watch more learner stories →',
  alwOfferKicker: "The Adult Learners' Week deal",
  alwOfferTitle: 'Try everything. Free for 30 days.',
  alwOfferBody: "Your free 30 days unlock every course we've built – Welsh, Spanish, French, Italian, German, Portuguese, Japanese, Korean, Mandarin Chinese, Arabic, and English as a second language.",
  alwOffer1: '30 days completely free – no card details at all',
  alwOffer2: 'Every language and every level included',
  alwOffer3: "Nothing to cancel – if you don't carry on, it simply stops",
  alwOfferCta: 'Start my free 30 days',
  alwOfferFine: "After your 30 days, carry on from £15 a month if you loved it. If not, you don't need to do anything – we never took a card, so there's nothing to cancel.",
  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Hero', keys: ['alwKicker','alwTitle','alwLede','alwCta','alwTrust'], long:['alwLede'] },
  { name: 'Why', keys: ['alwWhyKicker','alwWhyTitle','alwWhy1Title','alwWhy1Body','alwWhy2Title','alwWhy2Body','alwWhy3Title','alwWhy3Body'], long:['alwWhy1Body','alwWhy2Body','alwWhy3Body'] },
  { name: 'Testimonial', keys: ['testiKicker','testiQuote','testiName','testiRole','testiCredit','testiMoreLink'], long:['testiQuote'] },
  { name: 'Offer', keys: ['alwOfferKicker','alwOfferTitle','alwOfferBody','alwOffer1','alwOffer2','alwOffer3','alwOfferCta','alwOfferFine'], long:['alwOfferBody','alwOfferFine'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

export default function Alw() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });

  // Tag the whole visit as Nick's ALW cohort, whatever path they take to /pay.
  useEffect(() => { storeSource(ALW_SOURCE); }, []);

  return (
    <div data-screen-label="Adult Learners Week">
      <AlwTickStyleInjector/>
      <Header t={tweaks}/>
      <main>
        <AlwHero t={tweaks}/>
        <AlwWhy t={tweaks}/>
        <TestimonialFeature t={tweaks}/>
        <AlwOffer t={tweaks}/>
      </main>
      {/* hideCta: the footer's 7-day pitch would sit right under the
          30-day offer card and contradict it. */}
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
